<template>
  <v-card-text class="pa-2" v-if="isLoaded">
    <v-row no-gutters>
      <v-col>
        <app-search :search="ipMapping.search" v-model="ipMapping.filter" />
      </v-col>

      <div class="d-flex flex-row ga-1 flex-wrap ml-2">
        <v-btn
          v-permission.config
          color="primary"
          @click="showIpMappingRegister"
          >追加</v-btn
        >
        <v-btn v-permission.config color="primary" @click="deleteIpMapping"
          >削除</v-btn
        >
      </div>
    </v-row>
    <v-alert
      v-if="!!ipMappingAlertMsg"
      class="my-2"
      :text="ipMappingAlertMsg"
    />
    <div class="d-flex flex-row justify-end mb-2">
      <v-btn color="primary" @click="load">一覧更新</v-btn>
    </div>
    <app-table
      v-model:table-sort-by="ipMapping.sortBy"
      primary-key="virtualIpAddressSeq"
      :sort-null-last="true"
      :fields="ipMapping.fields"
      :items="ipMapping.items"
      :sort-desc="false"
      :filter="ipMapping.filter"
      :filter-option="ipMapping.filter_option"
      :selectable="selectable"
      :hover="true"
      @click:row="showIpMappingReference"
      :td-class="cellClass"
      sticky-header="50vh"
    >
      <template #cell(_selected)="data">
        <v-checkbox-btn
          v-if="!data.item.isPolicyInUse"
          v-permission.config
          :value="data.item"
          v-model="ipMapping.selected"
          @click.stop
        />
      </template>
      <template #cell(isPortForward)="data">
        {{ $filter.enumTo(data.value, "enable") }}
      </template>
      <template #cell(description)="data">
        {{ $filter.emptyTo(data.value, "-") }}
      </template>
      <template #cell(isPolicyInUse)="data">
        {{ $filter.enumTo(data.value, "isPolicyInUse") }}
      </template>
    </app-table>
  </v-card-text>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { IpMapping } from "@/apis/IpMappingApi";
import IpMappingType4Edit from "@/modals/applianceObject/IpMappingType4Edit.vue";
import IpMappingReference from "@/modals/applianceObject/IpMappingReference.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "Type4IpMapping",
  props: {
    /** Type4ID */
    type4Id: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      ipMappingAlertMsg: "",
      ipMappingKeyId: "",
      ipMapping: {
        sortBy: { key: "ipMappingName", order: "asc" } as SortItem,
        fields: [
          {
            key: "ipMappingName",
            title: "名前",
            width: "200px",
            sortable: true,
          },
          {
            key: "globalIpAddress",
            title: "グローバルIP",
            width: "200px",
            sortable: true,
          },
          {
            key: "privateIpAddress",
            title: "プライベートIP",
            width: "200px",
            sortable: true,
          },
          {
            key: "isPortForward",
            title: "ポートフォワード",
            width: "170px",
            sortable: true,
          },
          {
            key: "description",
            title: "備考",
            minWidth: "100px",
            sortable: true,
          },
          {
            key: "isPolicyInUse",
            title: "ポリシー使用有無",
            width: "170px",
            sortable: true,
          },
        ] as AppTableHeader<IpMapping>[],
        items: [] as IpMapping[],
        selected: [],
        search: {
          ipMappingName: { type: "text", label: "名前" },
          globalIpAddress: { type: "text", label: "グローバルIP" },
          privateIpAddress: { type: "text", label: "プライベートIP" },
          isPortForward: {
            type: "select",
            label: "ポートフォワード",
            items: [
              { value: true, text: "有効" },
              { value: false, text: "無効" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          ipMappingName: "like",
          globalIpAddress: "like",
          privateIpAddress: "like",
          isPortForward: "equal",
        } as FilterOption,
      },
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      const res = await this.$api.ipMapping.getType4IpMappingList(this.type4Id);
      this.ipMapping.items = res.ipMappingList;
      this.ipMappingKeyId = res.ipMappingKeyId;

      // チェックボックスの状態はデータを再取得しても残るので、ここでクリアしておく
      if (this.ipMapping.selected.length) {
        this.ipMapping.selected = [];
      }
    },

    /**
     * IPマッピング参照モーダルを表示する
     * @param row テーブルで選択された行データ
     */
    async showIpMappingReference(row: IpMapping) {
      this.ipMappingAlertMsg = "";
      await this.$modal.show(IpMappingReference, {
        ipMapping: row,
        ipMappingKeyId: this.ipMappingKeyId,
        type4Id: this.type4Id,
      });
      // 各タブの情報を再取得する
      this.$emit("reload");
    },

    /** IPマッピング追加モーダルを表示する */
    async showIpMappingRegister() {
      this.ipMappingAlertMsg = "";
      await this.$modal.show(IpMappingType4Edit, {
        ipMappingKeyId: this.ipMappingKeyId,
        type4Id: this.type4Id,
      });
      // 各タブの情報を再取得する
      this.$emit("reload");
    },

    /** IPマッピング削除確認モーダルを表示する */
    async deleteIpMapping() {
      // チェックボックスが選択1つも選択されていない場合、アラート表示して処理終了
      if (this.ipMapping.selected.length <= 0) {
        // アラートを表示する
        this.ipMappingAlertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        // アラートを非表示にする
        this.ipMappingAlertMsg = "";
      }

      await this.$confirm(
        `IPマッピングを${this.ipMapping.selected.length}件削除します。よろしいですか？`
      );
      await this.$api.ipMapping.deleteType4IpMapping(this.type4Id, {
        ipMappingList: this.ipMapping.selected.map(
          (e: IpMapping) => e.virtualIpAddressSeq
        ),
        ipMappingKeyId: this.ipMappingKeyId,
        note: null,
      });
      // 各タブの情報を再取得する
      this.$emit("reload");
    },

    /** エラーメッセージを初期化する(親画面から呼び出される) */
    async alertClear() {
      this.ipMappingAlertMsg = "";
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
